import React, { Fragment } from 'react';
import { useDispatch } from 'react-redux';
import { getDashboardData } from '../../redux/actions/dashboardActions';
import { getChartData } from '../../redux/actions/chartActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';
import Content from '../../components/Content';
import SelectBox from '../../components/SelectBox';
import DestinationCountries from './DestinationCountries';
import classes from './dashboard.module.css';
import SourceCountries from './SourceCountries';
import DestinationPort from './DestinationPort';
import DestinationIPs from './DestinationIPs';
import NetworkError from '../../components/Error';
import Loader from '../../components/Loader';

const FRONTEND_DASHBOARD_ERROR_CODES = {
  INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
  CHART_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
  WATCHER_DASHBOARD_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
  WATCHER_FILTER_INVALID: ErrorCodes.API_INVALID_PARAMETER,
};

const buildDashboardSupportError = (errorCode, pageInfo, sourceMessage) =>
  formatFrontendSupportError({
    errorCode,
    pageInfo,
    sourceMessage,
  });

const Dashboard = () => {
  const dispatch = useDispatch();

  const [initialLoading, setInitialLoading] = React.useState(true);
  const [chartDataLoading, setChartDataLoading] = React.useState(true);
  const [chartData, setChartData] = React.useState({});
  const [dashboardData, setDashboardData] = React.useState({});
  const [hasError, setHasError] = React.useState(false);
  const [supportErrorMessage, setSupportErrorMessage] = React.useState('');
  const [copiedSupportError, setCopiedSupportError] = React.useState(false);

  // Watcher selection state
  const [selectedWatcher, setSelectedWatcher] = React.useState('all');
  const [availableWatchers, setAvailableWatchers] = React.useState([]);

  // Chart configuration states
  const [topValueSourceCountries, setTopValueSourceCountries] = React.useState(10);
  const [topValueDestinationCountries, setTopValueDestinationCountries] = React.useState(10);
  const [topValueDestinationPort, setTopValueDestinationPort] = React.useState(10);
  const [topValueDestinationIPs, setTopValueDestinationIPs] = React.useState(10);

  const setDashboardSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
    setHasError(true);
    setSupportErrorMessage(buildDashboardSupportError(errorCode, pageInfo, sourceMessage));
    setCopiedSupportError(false);
  }, []);

  const clearDashboardSupportError = React.useCallback(() => {
    setHasError(false);
    setSupportErrorMessage('');
    setCopiedSupportError(false);
  }, []);

  const handleCopySupportError = React.useCallback(async () => {
    if (!supportErrorMessage) {
      return;
    }

    try {
      await navigator.clipboard.writeText(supportErrorMessage);
      setCopiedSupportError(true);
      setTimeout(() => setCopiedSupportError(false), 2000);
    } catch (error) {
      setCopiedSupportError(false);
    }
  }, [supportErrorMessage]);

  // Initial data fetch
  React.useEffect(() => {
    const getInitialData = async () => {
      setInitialLoading(true);
      clearDashboardSupportError();
      setHasError(false);

      try {
        const data = await dispatch(getDashboardData());
        if (data && data.status) {
          setDashboardData(data);
          setAvailableWatchers(data.watchers || []);
          setHasError(false);
          clearDashboardSupportError();
        } else {
          setDashboardSupportError(
            FRONTEND_DASHBOARD_ERROR_CODES.INITIAL_LOAD_FAILED,
            'Dashboard Page / Initial Data Load Failed',
            data?.msg || 'Dashboard initial data request did not return success'
          );
          setHasError(true);
        }
      } catch (error) {
        setHasError(true);
        console.error('Error fetching initial dashboard data:', error);
        setDashboardSupportError(
          FRONTEND_DASHBOARD_ERROR_CODES.INITIAL_LOAD_FAILED,
          'Dashboard Page / Initial Data Request Error',
          error?.message || String(error)
        );
      }
      
      setInitialLoading(false);
    };

    getInitialData();
  }, [dispatch, clearDashboardSupportError, setDashboardSupportError]);

  // Chart data fetch
  const getChartDataForWatcher = React.useCallback(async () => {
    setChartDataLoading(true);
    clearDashboardSupportError();
    setHasError(false);

    const formData = new FormData();
    formData.append('topSource', topValueSourceCountries);
    formData.append('topDest', topValueDestinationCountries);
    formData.append('topDestIP', topValueDestinationIPs);
    formData.append('topDestPort', topValueDestinationPort);

    // Add watcher filter if a specific watcher is selected
    if (selectedWatcher !== 'all') {
      formData.append('watchers', selectedWatcher);
    }

    try {
      const data = await dispatch(getChartData(formData));
      if (data && data.status) {
        setChartData(data);
        clearDashboardSupportError();
        setHasError(false);
      } else {
        setDashboardSupportError(
          FRONTEND_DASHBOARD_ERROR_CODES.CHART_LOAD_FAILED,
          'Dashboard Page / Chart Data Load Failed',
          data?.msg || 'Dashboard chart data request did not return success'
        );
        setHasError(true);
      }
    } catch (error) {
      setHasError(true);
      console.error('Error fetching chart data:', error);
      setDashboardSupportError(
        FRONTEND_DASHBOARD_ERROR_CODES.CHART_LOAD_FAILED,
        'Dashboard Page / Chart Data Request Error',
        error?.message || String(error)
      );
    }
    
    setChartDataLoading(false);
  }, [
    dispatch,
    clearDashboardSupportError,
    setDashboardSupportError,
    selectedWatcher,
    topValueSourceCountries,
    topValueDestinationCountries,
    topValueDestinationIPs,
    topValueDestinationPort,
  ]);

  // Dashboard data fetch based on watcher selection
  const getDashboardDataForWatcher = React.useCallback(async () => {
    if (!selectedWatcher) {
      setDashboardSupportError(
        FRONTEND_DASHBOARD_ERROR_CODES.WATCHER_FILTER_INVALID,
        'Dashboard Page / Watcher Filter Invalid',
        'Watcher filter selection is empty'
      );
      return;
    }

    if (selectedWatcher === 'all') {
      // For "all watchers", use GET request
      try {
        const data = await dispatch(getDashboardData());
        if (data && data.status) {
          setDashboardData(data);
          clearDashboardSupportError();
        } else {
          setDashboardSupportError(
            FRONTEND_DASHBOARD_ERROR_CODES.WATCHER_DASHBOARD_LOAD_FAILED,
            'Dashboard Page / All Watchers Data Load Failed',
            data?.msg || 'Dashboard all-watchers request did not return success'
          );
        }
      } catch (error) {
        console.error('Error fetching dashboard data for all watchers:', error);
        setDashboardSupportError(
          FRONTEND_DASHBOARD_ERROR_CODES.WATCHER_DASHBOARD_LOAD_FAILED,
          'Dashboard Page / All Watchers Data Request Error',
          error?.message || String(error)
        );
      }
    } else {
      // For specific watcher, use POST request
      const formData = new FormData();
      formData.append('watchers', selectedWatcher);

      try {
        const data = await dispatch(getDashboardData(formData));
        if (data && data.status) {
          setDashboardData(data);
          clearDashboardSupportError();
        } else {
          setDashboardSupportError(
            FRONTEND_DASHBOARD_ERROR_CODES.WATCHER_DASHBOARD_LOAD_FAILED,
            'Dashboard Page / Watcher Data Load Failed',
            data?.msg || 'Dashboard watcher-filtered request did not return success'
          );
        }
      } catch (error) {
        console.error('Error fetching dashboard data for watcher:', error);
        setDashboardSupportError(
          FRONTEND_DASHBOARD_ERROR_CODES.WATCHER_DASHBOARD_LOAD_FAILED,
          'Dashboard Page / Watcher Data Request Error',
          error?.message || String(error)
        );
      }
    }
  }, [dispatch, selectedWatcher, clearDashboardSupportError, setDashboardSupportError]);

  // Fetch data when watcher selection changes
  React.useEffect(() => {
    if (availableWatchers.length > 0) {
      getDashboardDataForWatcher();
      getChartDataForWatcher();
    }
  }, [selectedWatcher, getDashboardDataForWatcher, getChartDataForWatcher, availableWatchers.length]);

  // Handle watcher selection change
  const handleWatcherChange = (e) => {
    setSelectedWatcher(e.target.value);
  };

  // If there's a network error and no data, show error
  if (hasError && Object.keys(chartData).length === 0 && Object.keys(dashboardData).length === 0) {
    return (
      <Content currentMenu='dashboard' headerTitle='Dashboard'>
        <NetworkError />
        {supportErrorMessage && (
          <div className={classes.supportErrorCard}>
            <strong>Support Error:</strong>
            <code className={classes.supportErrorCode}>{supportErrorMessage}</code>
            <button
              type='button'
              className={classes.copySupportErrorButton}
              onClick={handleCopySupportError}
            >
              {copiedSupportError ? 'Copied' : 'Copy Error Details'}
            </button>
          </div>
        )}
      </Content>
    );
  }

  return (
    <Content currentMenu='dashboard' headerTitle='Dashboard'>
      {initialLoading && Object.keys(dashboardData).length === 0 ? (
        <Loader />
      ) : (
        <Fragment>
          {hasError && supportErrorMessage && (
            <div className={classes.supportErrorBanner}>
              <div className={classes.supportErrorBannerText}>
                <strong>Support Error:</strong>
                <code className={classes.supportErrorCode}>{supportErrorMessage}</code>
              </div>
              <button
                type='button'
                className={classes.copySupportErrorButton}
                onClick={handleCopySupportError}
              >
                {copiedSupportError ? 'Copied' : 'Copy Error Details'}
              </button>
            </div>
          )}

          {/* Watcher Selection Dropdown */}
          <div className={classes.filterContainer}>
            <div className={classes.filterContainer_box}>
              <SelectBox
                label='Select Watcher'
                value={selectedWatcher}
                onChange={handleWatcherChange}
              >
                <option value='all'>All Watchers</option>
                {availableWatchers && availableWatchers.length > 0 ? (
                  availableWatchers.map((watcher, key) =>
                    watcher.length >= 2 && (
                      <option value={watcher[0]} key={watcher[0] + key}>
                        {watcher[1]}
                      </option>
                    )
                  )
                ) : (
                  <option value=''>No watchers found</option>
                )}
              </SelectBox>
            </div>
          </div>

          {/* Dashboard Content */}
          <div className={classes.content}>
            <SourceCountries
              data={{
                topValueSourceCountries,
                setTopValueSourceCountries,
                dataSource: chartData.sourceCountries,
                loading: chartDataLoading
              }}
            />

            <DestinationCountries
              data={{
                topValueDestinationCountries,
                setTopValueDestinationCountries,
                dataSource: chartData.destinationCountries,
                loading: chartDataLoading
              }}
            />

            <DestinationPort
              data={{
                topValueDestinationPort,
                setTopValueDestinationPort,
                dataSource: chartData.destinationPorts,
                loading: chartDataLoading
              }}
            />

            <DestinationIPs
              data={{
                topValueDestinationIPs,
                setTopValueDestinationIPs,
                dataSource: chartData.destinationIPs,
                loading: chartDataLoading
              }}
            />
          </div>
        </Fragment>
      )}
    </Content>
  );
};

export default Dashboard;
